import { useState } from 'react'
import { useResolveEmergency } from '../api/emergencies'
import type { EmergencyAlert } from '../api/types'
import { ConfirmDialog } from './Modal'
import { useToast } from './Toast'
import { errorMessage } from '../lib/problem'
import { t } from '../i18n'

/** "Mark resolved" for one alert, behind a confirmation: clearing an SOS takes it off every manager's screen, so a
 * stray click must not do it. The styling comes from where the button sits (the red banner or a table row). */
export function ResolveAlertButton({ alert, className }: { alert: EmergencyAlert; className: string }) {
  const [confirming, setConfirming] = useState(false)
  const toast = useToast()
  const resolve = useResolveEmergency()

  function confirm() {
    setConfirming(false)
    // A promise chain rather than mutate()'s callbacks: this button disappears the moment the alert clears, and those
    // callbacks are dropped once the component is gone, which would lose the toast.
    resolve
      .mutateAsync({ siteId: alert.siteId, alertId: alert.id })
      .then(() => toast.success(t('emergency.resolvedToast')))
      .catch((e: unknown) => toast.error(errorMessage(e)))
  }

  return (
    <>
      <button type="button" className={className} disabled={resolve.isPending} onClick={() => setConfirming(true)}>
        {t('emergency.resolve')}
      </button>
      <ConfirmDialog
        open={confirming}
        title={t('emergency.resolveTitle')}
        message={t('emergency.resolveMessage', { name: alert.employeeName })}
        confirmLabel={t('emergency.resolve')}
        tone="primary"
        onConfirm={confirm}
        onCancel={() => setConfirming(false)}
      />
    </>
  )
}
